import type { UploadFile } from "antd"
import type { Dayjs } from "dayjs"

import type { SubcontractorFormData } from "./subcontractor-form-data.ts"

export const DATE_FIELDS = [
  "workers_comp_expiration_date",
  "general_liability_expiration_date",
  "license_expiration_date",
  "w9_validity_date",
  "cslb_issue_date"
] as const

export const FILE_FIELDS = [
  "workers_comp_document",
  "general_liability_document",
  "license_document",
  "w9_document"
] as const

type DateField = (typeof DATE_FIELDS)[number]
type FileField = (typeof FILE_FIELDS)[number]

/** What the antd form holds: pickers hand back Dayjs, uploads a file list. */
export type SubcontractorFormValues = Omit<
  SubcontractorFormData,
  DateField | FileField
> &
  Partial<Record<DateField, Dayjs | null>> &
  Partial<Record<FileField, UploadFile[]>>

/**
 * Fields whose emptied state antd reports as `undefined`/`null` (a cleared
 * select, date or number) — which a JSON body drops and a multipart body skips,
 * so on edit the stored value would silently survive the user clearing it.
 * Secrets and files are deliberately absent: blank never wipes those.
 */
const CLEARABLE_ON_EDIT = [
  "price_per_hour",
  "legal_business_name",
  "entity_type",
  "business_address",
  "business_city",
  "business_state",
  "business_zip_code",
  "w9_validity_date",
  "primary_contact_name",
  "primary_contact_email",
  "primary_contact_phone",
  "cslb_license_number",
  "cslb_classification",
  "cslb_status",
  "cslb_issue_date",
  "cslb_bond_amount",
  "cslb_bond_number",
  "cslb_qualifier_bond_amount",
  "cslb_qualifier_bond_number",
  "payment_method"
] as const satisfies readonly (keyof SubcontractorFormData)[]

const isBlank = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0)

export const toSubcontractorFormData = (
  values: SubcontractorFormValues,
  initialValues?: Partial<SubcontractorFormData>
): SubcontractorFormData => {
  const dates = Object.fromEntries(
    DATE_FIELDS.map((field) => {
      const value = values[field]
      return [
        field,
        value && typeof value.format === "function"
          ? value.format("YYYY-MM-DD")
          : undefined
      ]
    })
  ) as Partial<Record<DateField, string>>

  const files = Object.fromEntries(
    FILE_FIELDS.map((field) => [
      field,
      values[field]?.[0]?.originFileObj as File | undefined
    ])
  ) as Partial<Record<FileField, File>>

  // The runtime object still carries the Dayjs/fileList values; `dates` and
  // `files` always set every one of those keys, so they are overwritten.
  const base: Omit<SubcontractorFormValues, DateField | FileField> = values
  const payload: SubcontractorFormData = { ...base, ...dates, ...files }

  if (initialValues) {
    const cleared = CLEARABLE_ON_EDIT.filter(
      (field) => !isBlank(initialValues[field]) && payload[field] == null
    )
    return { ...payload, ...Object.fromEntries(cleared.map((f) => [f, null])) }
  }

  return payload
}
